/**
 * Tech-debt #4 — Display Messages family (OCPP 2.0.1, Session 19d):
 * Set / Clear / GetDisplayMessages, persisted to the displayMessages table and
 * mirrored into the reactive store.
 */
import { errorMessage } from '@/lib/errorMessage';
import { parseMessageInfo } from '@/domain/DisplayMessage';
import { OcppV201Adapter } from '@/protocol/v201/OcppV201Adapter';
import { paginateNotifyDisplayMessages } from '@/protocol/v201/actions/notifyDisplayMessages';
import { useDisplayMessageStore } from '../useDisplayMessageStore';
import { useStationStore } from '../useStationStore';
import type { CsmsCtx, CsmsEvent } from './context';
import { displayMessageRepo, maxReportItems, varValuesRepo } from './shared';

export async function handleSetDisplayMessage(
  event: CsmsEvent<'SetDisplayMessage'>,
  ctx: CsmsCtx,
): Promise<void> {
  // Parse the MessageInfo, persist (replace-by-id), reply. SetDisplayMessage
  // Status: Accepted | Rejected | NotSupported*.
  const snap = parseMessageInfo(event.message);
  if (!snap) {
    ctx.dispatcher.reply(ctx.messageId, { status: 'Rejected' });
    return;
  }
  await displayMessageRepo.upsert(ctx.stationId, snap);
  await useDisplayMessageStore.getState().refresh(ctx.stationId);
  ctx.dispatcher.reply(ctx.messageId, { status: 'Accepted' });
}

export async function handleClearDisplayMessage(
  event: CsmsEvent<'ClearDisplayMessage'>,
  ctx: CsmsCtx,
): Promise<void> {
  // ClearMessageStatus: Accepted | Unknown.
  const existed = await displayMessageRepo.delete(ctx.stationId, event.id);
  await useDisplayMessageStore.getState().refresh(ctx.stationId);
  ctx.dispatcher.reply(ctx.messageId, { status: existed ? 'Accepted' : 'Unknown' });
}

export async function handleGetDisplayMessages(
  event: CsmsEvent<'GetDisplayMessages'>,
  ctx: CsmsCtx,
): Promise<void> {
  const { adapter, dispatcher, stationId, messageId } = ctx;
  // GetDisplayMessagesStatus: Accepted | Unknown. Filter by id / priority /
  // state, reply Accepted + stream paginated NotifyDisplayMessages, or Unknown.
  if (!(adapter instanceof OcppV201Adapter)) {
    dispatcher.replyError(messageId, 'NotImplemented', 'GetDisplayMessages is v2.0.1-only', {});
    return;
  }
  const allDM = await displayMessageRepo.byStation(stationId);
  const matched = allDM.filter((m) => {
    if (event.ids !== undefined && event.ids.length > 0 && !event.ids.includes(m.id)) return false;
    if (event.priority !== undefined && m.priority !== event.priority) return false;
    if (event.state !== undefined && m.state !== event.state) return false;
    return true;
  });
  if (matched.length === 0) {
    dispatcher.reply(messageId, { status: 'Unknown' });
    return;
  }
  dispatcher.reply(messageId, { status: 'Accepted' });
  const rowsDM = await varValuesRepo.byStation(stationId);
  const pages = paginateNotifyDisplayMessages(event.requestId, matched, maxReportItems(rowsDM));
  try {
    for (const page of pages) {
      await adapter.notifyDisplayMessages(page).response;
    }
  } catch (err) {
    useStationStore.setState({ lastError: errorMessage(err) });
  }
}
